import { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/Button";
import { IApiResponse } from "../../../@types/api-response.type";
import vectorIcon from "../../../assets/_VectorIcon_.svg";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import { IPosition } from "../../../@types/position.type";
import { getAllPositions } from "../../../services/api/positions/PositionsService";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import usePermission from "../../../hooks/usePermission";
import MuiButton from "@mui/material/Button";
import { AgGridReact } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { ColDef, FilterChangedEvent } from "ag-grid-community";
import styles from "./styles.module.scss";
import { useSyncActive } from "../../../utils/useSyncActive";

export const Positions: React.FC = () => {
  const [positionsItems, setPositionsItems] = useState<IPosition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const gridRef = useRef<AgGridReact<any>>(null);

  const isSyncActive = useSyncActive();

  const permissions = usePermission(FeatureStatic.positions.id);

  const navigate = useNavigate();

  const columns: ColDef[] = [
    {
      field: "positionName",
      headerName: "Nome",
      minWidth: 300,
      flex: 1,
    },
  ];

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersPositions")
  );
  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortPositions")
  );

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersPositions", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortPositions", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFilters) {
        gridApi.setFilterModel(cachedFilters);
      }

      // Restaurar ordenação
      if (cachedSort) {
        gridApi.applyColumnState({
          state: cachedSort,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFilters, cachedSort, columns]);

  useEffect(() => {
    async function fetchAllPositions() {
      try {
        setLoading(true);

        const response = (await getAllPositions()) as IApiResponse<IPosition[]>;

        setPositionsItems(response.result);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    if (!permissions.canView()) {
      navigate("/unauthorized");
    }
    fetchAllPositions();
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  return (
    <div className={styles.position}>
      <div className={styles.back}>
        <MuiButton
          className={styles.buttonMui}
          onClick={() => navigate("/configurations")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </MuiButton>
      </div>
      <div className={styles.titleAndBtnCreate}>
        <h1>Funções</h1>
        <div className={styles.btnsFilterAndCreate}>
          {!isSyncActive && (
            <Button
              iconUrl={addCircleImage}
              onClick={() => {
                navigate("/configurations/positions/create");
              }}
              content="Nova Função"
              className={styles.button}
            />
          )}
        </div>
      </div>

      <div className={`${styles.dataGridContainer} `}>
        <AgGridReact
          ref={gridRef}
          rowData={positionsItems}
          columnDefs={columns}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: isSyncActive ? "default" : "pointer" }}
          onRowClicked={(row) => {
            if (!isSyncActive) navigateToUpdatePage(row.data.id);
          }}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          animateRows={true}
        />
      </div>
    </div>
  );
};
